import { devModeMocks } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { isCurrentCompanyMembersRequest } from "@src/store/members/members-slice-helper";
/**
 * Fetches company members from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails and not in dev mode
 */
export const fetchCompanyMembers = createAsyncThunk("members/fetchCompanyMembers", async ({
  companyId
}, {
  getState,
  requestId
}) => {
  const {
    app: {
      isDevModeEnabled
    }
  } = getState();
  try {
    /**
     * Note: Currently, we are fetching all users from the endpoint, including user groups.
     * Once the backend updates the teams endpoint with roles,
     * we can remove user groups from this fetch to optimize the request.
     */
    return await faroCoreApiClient.company.getMembersInCompany({
      companyId,
      types: ["user", "user-group", "user-invite"]
    });
  } catch (error) {
    // The outcome of a request that the members were invalidated or reset during is ignored, see
    // membersSlice, so its failure is not reported either, e.g. after leaving its workspace
    if (!isCurrentCompanyMembersRequest(getState().members, requestId)) {
      return [];
    }
    if (isDevModeEnabled) {
      return devModeMocks.members;
    }
    throw error;
  }
}, {
  condition: (_, api) => {
    const {
      hasFetchedWorkspaceMembers
    } = api.getState().members.fetched;
    const {
      companyMembersRequestId
    } = api.getState().members.fetching;

    // Skip fetching if members have already been fetched or a request is fetching them. One that
    // they were invalidated during does not count, since its outcome is ignored.
    return !hasFetchedWorkspaceMembers && companyMembersRequestId === undefined;
  }
});

/**
 * Fetches the details of a company member so it can be put into the members store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberDetails = createAsyncThunk("members/fetchCompanyMemberDetails", async ({
  companyId,
  identity
}) => {
  return await faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId,
    identity
  });
});

/**
 * Remove a member from the company using the backend and then removes it from the store as well
 * @throws {Error} if no companyId or member email exist to remove the member from the workspace
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromCompany = createAsyncThunk("members/removeCompanyMember", async ({
  companyId,
  member
}) => {
  if (!companyId) {
    throw new Error("No companyId was given to remove member from workspace!");
  }
  if (!member?.email) {
    throw new Error("No email was given to remove member from workspace!");
  }
  await faroCoreApiClient.company.removeMemberFromCompany({
    companyId,
    userIdentity: member.email
  });
  return member;
});

/**
 * Update a member role in the workspace using the backend,
 * and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateMemberRoleInWorkspace = createAsyncThunk("members/updateMemberRoleInWorkspace", async ({
  companyId,
  role,
  identity
}) => {
  const memberData = {
    role,
    identity
  };
  const result = await faroCoreApiClient.company.updateCompanyMember({
    companyId,
    identity,
    payload: memberData
  });
  return {
    role: result.role,
    // The client returns a response that misses it rather than reject it, while the role just
    // requested is the one assigned to the member then
    userRole: result.userRole ?? role,
    identity: result.identity
  };
});
/**
 * Set the permissions of a member in the company using the backend.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const setMemberPermissionsInCompany = createAsyncThunk("members/setMemberPermissionsInCompany", async ({
  companyId,
  userIdentity,
  permissions
}) => {
  return await faroCoreApiClient.company.setMemberPermissionsInCompany({
    companyId,
    userIdentity,
    payload: {
      permissions
    }
  });
});